import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChild
} from '@angular/core';

import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/take';
import 'rxjs/add/operator/takeUntil';

import {
  getValue
} from 'microedge-rxstate/dist/helpers';

import {
  SkyListFilterSummaryComponent,
  SkyListFilterInlineComponent
} from '../list-filters';

import {
  ListToolbarModel,
  ListToolbarItemModel,
  ListToolbarSetTypeAction,
  ListState,
  ListStateDispatcher,
  ListSortLabelModel,
  ListSortFieldSelectorModel,
  ListFilterModel,
  ListPagingSetPageNumberAction
} from '../list/state';

import {
  SkySearchComponent
} from '../search';

import {
  ListToolbarConfigSetSearchEnabledAction,
  ListToolbarConfigSetSortSelectorEnabledAction
} from './state/config/actions';

import {
  ListToolbarState,
  ListToolbarStateDispatcher,
  ListToolbarStateModel
} from './state';

import { SkyListToolbarItemComponent } from './list-toolbar-item.component';
import { SkyListToolbarSortComponent } from './list-toolbar-sort.component';


export class SkyListToolbarComponent implements OnInit, AfterContentInit, OnDestroy {
  
  public placeholder: string;

  
  public searchEnabled: boolean | Observable<boolean>;

  
  public searchComponent: SkySearchComponent;

  
  public sortSelectorEnabled: boolean | Observable<boolean>;

  
  public toolbarType: string = 'standard';

  
  public searchText: string | Observable<string>;

  public sortSelectors: Observable<Array<any>>;
  public searchTextInput: Observable<string>;
  public view: Observable<string>;
  public leftTemplates: ListToolbarItemModel[];
  public centerTemplates: ListToolbarItemModel[];
  public rightTemplates: ListToolbarItemModel[];
  public type: Observable<string>;
  public isSearchEnabled: Observable<boolean>;
  public isSortSelectorEnabled: Observable<boolean>;
  public appliedFilters: Observable<Array<ListFilterModel>>;
  public hasAppliedFilters: Observable<boolean>;
  public showFilterSummary: boolean;
  public hasInlineFilters: boolean;
  public inlineFilterBarExpanded: boolean = false;
  public hasAdditionalToolbarSection = false;

  
  private toolbarItems: QueryList<SkyListToolbarItemComponent>;

  
  private toolbarSorts: QueryList<SkyListToolbarSortComponent>;

  
  private filterSummary: QueryList<SkyListFilterSummaryComponent>;

  
  private inlineFilter: QueryList<SkyListFilterInlineComponent>;

  
  private searchTemplate: TemplateRef<any>;

  
  private sortSelectorTemplate: TemplateRef<any>;

  
  private inlineFilterButtonTemplate: TemplateRef<any>;

  private hasSortSelectors: boolean = false;
  private ngUnsubscribe = new Subject();

  constructor(
    private changeDetector: ChangeDetectorRef,
    private state: ListState,
    private dispatcher: ListStateDispatcher,
    private toolbarState: ListToolbarState,
    public toolbarDispatcher: ListToolbarStateDispatcher
  ) { }

  public ngOnInit() {
    this.dispatcher.toolbarExists(true);

    getValue(this.searchText, (searchText: string) => {
      this.updateSearchText(searchText);
    });

    getValue(this.searchEnabled, (searchEnabled: boolean) => {
      this.toolbarDispatcher.next(
        new ListToolbarConfigSetSearchEnabledAction(
          searchEnabled === undefined ? true : searchEnabled
        )
      );
    });

    getValue(this.toolbarType, (type: string) => {
      this.dispatcher.next(new ListToolbarSetTypeAction(this.toolbarType));
    });

    getValue(this.sortSelectorEnabled, (sortSelectorEnabled: any) => {
      this.toolbarDispatcher.next(
        new ListToolbarConfigSetSortSelectorEnabledAction(
          sortSelectorEnabled === undefined ? true : sortSelectorEnabled
        )
      );
    });

    this.sortSelectors = this.getSortSelectors();

    // Initialize the sort toolbar item if necessary
    this.sortSelectors
      .takeUntil(this.ngUnsubscribe)
      .distinctUntilChanged()
      .subscribe((currentSort) => {
        if (currentSort.length > 0 && !this.hasSortSelectors) {
          this.hasSortSelectors = true;
          this.dispatcher.toolbarAddItems([
            new ListToolbarItemModel({
              id: 'sort-selector',
              template: this.sortSelectorTemplate,
              location: 'right'
            })
          ], 0);
        } else if (currentSort.length < 1 && this.hasSortSelectors) {
          this.hasSortSelectors = false;
          this.dispatcher.toolbarRemoveItems([
            'sort-selector'
          ]);
        }
      });

    this.searchTextInput = this.state.map(s => s.search.searchText).distinctUntilChanged();

    this.view = this.state.map(s => s.views.active).distinctUntilChanged();

    this.watchTemplates();

    this.type = this.state.map((state) => state.toolbar.type).distinctUntilChanged();

    this.type
      .takeUntil(this.ngUnsubscribe)
      .subscribe((toolbarType) => {
        if (toolbarType === 'search') {
          this.dispatcher.toolbarRemoveItems(['search']);
        } else {
          this.dispatcher.toolbarAddItems([
            new ListToolbarItemModel({
              id: 'search',
              template: this.searchTemplate,
              location: 'center'
            })
          ]);
        }
      });

    this.isSearchEnabled = this.toolbarState.map(s => s.config)
      .distinctUntilChanged().map(c => c.searchEnabled);

    this.isSortSelectorEnabled = this.toolbarState.map(s => s.config)
      .distinctUntilChanged().map(c => c.sortSelectorEnabled);

    this.hasAppliedFilters = this.state
      .map(s => s.filters)
      .distinctUntilChanged()
      .map((filters) => {
        let activeFilters = filters.filter((f) => {
            return f.value !== '' &&
              f.value !== undefined &&
              f.value !== false &&
              f.value !== f.defaultValue;
          });
        return activeFilters.length > 0;
      });

    this.state
      .takeUntil(this.ngUnsubscribe)
      .subscribe((current: any) => {
        this.hasAdditionalToolbarSection = (current.toolbar.items.length > 0);
        this.changeDetector.detectChanges();
      });
  }

  public ngAfterContentInit() {
    this.toolbarItems.forEach((toolbarItem) => {
      this.dispatcher.toolbarAddItems(
        [new ListToolbarItemModel(toolbarItem)],
        toolbarItem.index
      );
    });

    let sortModels = this.toolbarSorts.map(sort =>
      new ListSortLabelModel(
        {
          text: sort.label,
          fieldSelector: sort.field,
          fieldType: sort.type,
          global: true,
          descending: sort.descending
        }
      )
    );

    this.dispatcher.sortSetGlobal(sortModels);

    this.showFilterSummary = (this.filterSummary.length > 0);
    this.hasInlineFilters = (this.inlineFilter.length > 0);

    if (this.hasInlineFilters) {
      this.dispatcher.toolbarAddItems([
        new ListToolbarItemModel({
          template: this.inlineFilterButtonTemplate,
          location: 'right'
        })
      ], 0);
    }
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public setSort(sort: ListSortLabelModel): void {
    this.dispatcher.sortSetFieldSelectors(
      [{fieldSelector: sort.fieldSelector, descending: sort.descending}]
    );
  }

  public inlineFilterButtonClick() {
    this.inlineFilterBarExpanded = !this.inlineFilterBarExpanded;
  }

  public updateSearchText(searchText: string) {
    this.state.take(1).subscribe((currentState) => {
      this.dispatcher.searchSetText(searchText);
      if (currentState.paging.pageNumber && currentState.paging.pageNumber !== 1) {
        this.dispatcher.next(
          new ListPagingSetPageNumberAction(Number(1))
        );
      }
    });
  }

  private itemIsInView(itemView: string, activeView: string) {
    return (itemView === undefined || itemView === activeView);
  }

  private getSortSelectors() {
    return Observable.combineLatest(
      this.state.map(s => s.sort.available).distinctUntilChanged(),
      this.state.map(s => s.sort.global).distinctUntilChanged(),
      this.state.map(s => s.sort.fieldSelectors).distinctUntilChanged(),
      (
        available: Array<ListSortLabelModel>,
        global: Array<ListSortLabelModel>,
        fieldSelectors: Array<ListSortFieldSelectorModel>
      ) => {

        // Get sorts that are in the global that are not in the available
        let sorts = global.filter(
          g => available.filter(a => a.fieldSelector === g.fieldSelector).length === 0
        );

        let resultSortSelectors = [...sorts, ...available].map(sortLabels => {
          let fs = fieldSelectors.filter(f => {
            return f.fieldSelector === sortLabels.fieldSelector
              && f.descending === sortLabels.descending;
          });
          let selected = false;
          if (fs.length > 0) {
            selected = true;
          }

          return {
            sort: sortLabels,
            selected: selected
          };
        });

        return resultSortSelectors;
      });
  }

  private watchTemplates() {
    const templateStream = Observable.combineLatest(
      this.state.map(s => s.toolbar).distinctUntilChanged(),
      this.view.distinctUntilChanged(),
      (toolbar: ListToolbarModel, view: string) => {
        const items = toolbar.items.filter((i: ListToolbarItemModel) => {
          return this.itemIsInView(i.view, view);
        });

        const templates: any = {};

        items.forEach((item: ListToolbarItemModel) => {
          templates[item.location] = templates[item.location] || [];
          templates[item.location].push(item);
        });

        return templates;
      }
    );

    templateStream
      .takeUntil(this.ngUnsubscribe)
      .subscribe((value) => {
        this.leftTemplates = value.left;
        this.centerTemplates = value.center;
        this.rightTemplates = value.right;
        this.changeDetector.markForCheck();
      });
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'sky-list-toolbar',
  template: `<div class="sky-list-toolbar-container">
  <sky-toolbar
    *ngIf="(type | async) !== 'search'">
    <sky-toolbar-section>
      <sky-toolbar-item
        *ngFor="let item of leftTemplates">
        <sky-list-toolbar-item-renderer
          [template]="item.template"
          [attr.sky-cmp-id]="item.id">
        </sky-list-toolbar-item-renderer>
      </sky-toolbar-item>
      <sky-toolbar-item
        *ngFor="let item of centerTemplates">
        <sky-list-toolbar-item-renderer
          [template]="item.template"
          [attr.sky-cmp-id]="item.id">
        </sky-list-toolbar-item-renderer>
      </sky-toolbar-item>
      <sky-toolbar-item
        *ngFor="let item of rightTemplates"
        [attr.sky-toolbar-id]="item.id">
        <sky-list-toolbar-item-renderer
          [template]="item.template"
          [attr.sky-cmp-id]="item.id">
        </sky-list-toolbar-item-renderer>
      </sky-toolbar-item>
      <ng-content></ng-content>
    </sky-toolbar-section>
    <sky-toolbar-section
      *ngIf="showFilterSummary && (hasAppliedFilters | async)">
      <ng-content
        select="sky-list-filter-summary">
      </ng-content>
    </sky-toolbar-section>
  </sky-toolbar>

  <div
    class="sky-list-toolbar-search"
    *ngIf="(type | async) === 'search'">
    <sky-toolbar>
      <sky-toolbar-section>
        <sky-toolbar-item>
          <sky-list-toolbar-item-renderer
            [template]="search"
            sky-cmp-id="search">
          </sky-list-toolbar-item-renderer>
        </sky-toolbar-item>
      </sky-toolbar-section>
      <sky-toolbar-section
        [hidden]="!hasAdditionalToolbarSection">
        <sky-toolbar-item
          *ngFor="let item of leftTemplates">
          <sky-list-toolbar-item-renderer
            [template]="item.template"
            [attr.sky-cmp-id]="item.id">
          </sky-list-toolbar-item-renderer>
        </sky-toolbar-item>
        <sky-toolbar-item
          *ngFor="let item of centerTemplates">
          <sky-list-toolbar-item-renderer
            *ngIf="item.id !== 'search'"
            [template]="item.template"
            [attr.sky-cmp-id]="item.id">
          </sky-list-toolbar-item-renderer>
        </sky-toolbar-item>
        <sky-toolbar-item
          *ngFor="let item of rightTemplates"
          [attr.sky-toolbar-id]="item.id">
          <sky-list-toolbar-item-renderer
            [template]="item.template"
            [attr.sky-cmp-id]="item.id">
          </sky-list-toolbar-item-renderer>
        </sky-toolbar-item>
        <ng-content></ng-content>
      </sky-toolbar-section>
    </sky-toolbar>
  </div>
  <div
    *ngIf="hasInlineFilters && inlineFilterBarExpanded">
    <ng-content
      select="sky-list-filter-inline">
    </ng-content>
  </div>
</div>

<ng-template #search>
  <div
    *ngIf="isSearchEnabled | async">
    <sky-search
      #searchComponent
      [expandMode]="(type | async) === 'search' ? 'fit' : 'responsive'"
      [searchText]="searchTextInput | async"
      (searchApply)="updateSearchText($event)"
      [placeholderText]="placeholder">
    </sky-search>
  </div>
</ng-template>

<ng-template #sortSelector>
  <div
    class="sky-toolbar-item-sort-container"
    *ngIf="(isSortSelectorEnabled | async) && (sortSelectors | async).length > 0">
    <sky-sort>
      <sky-sort-item
        *ngFor="let item of sortSelectors | async"
        [active]="item.selected"
        (itemSelect)="setSort(item.sort)">
        {{item.sort.text}}
      </sky-sort-item>
    </sky-sort>
  </div>
</ng-template>

<ng-template #inlineFilterButton>
  <sky-filter-button
    (filterButtonClick)="inlineFilterButtonClick()"
    [active]="hasAppliedFilters | async">
  </sky-filter-button>
</ng-template>
`,
  styles: [`.sky-list-toolbar-search /deep/ .sky-toolbar-container, .sky-list-toolbar-search /deep/ .sky-search-dismiss-absolute {
  background-color: #eeeeef;
}

.sky-list-toolbar-container /deep/ sky-toolbar-item[sky-toolbar-id="sort-selector"] .sky-toolbar-item {
  margin-right: 0;
}

.sky-toolbar-item-sort-container {
  margin-right: 5px;
}
`],
  providers: [
    ListToolbarState,
    ListToolbarStateDispatcher,
    ListToolbarStateModel
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ChangeDetectorRef, },
{type: ListState, },
{type: ListStateDispatcher, },
{type: ListToolbarState, },
{type: ListToolbarStateDispatcher, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'placeholder': [{ type: Input },],
'searchEnabled': [{ type: Input },],
'searchComponent': [{ type: ViewChild, args: ['searchComponent', ] },],
'sortSelectorEnabled': [{ type: Input },],
'toolbarType': [{ type: Input },],
'searchText': [{ type: Input },],
'toolbarItems': [{ type: ContentChildren, args: [SkyListToolbarItemComponent, ] },],
'toolbarSorts': [{ type: ContentChildren, args: [SkyListToolbarSortComponent, ] },],
'filterSummary': [{ type: ContentChildren, args: [SkyListFilterSummaryComponent, ] },],
'inlineFilter': [{ type: ContentChildren, args: [SkyListFilterInlineComponent, ] },],
'searchTemplate': [{ type: ViewChild, args: ['search', ] },],
'sortSelectorTemplate': [{ type: ViewChild, args: ['sortSelector', ] },],
'inlineFilterButtonTemplate': [{ type: ViewChild, args: ['inlineFilterButton', ] },],
};
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
